import gmapPromise from './googlemaps.js';

export const AUTOCOMPLETE_PREFERENCE = {
  country: 0,
  administrative_area_level_1: 1,
  locality: 2,
  vicinity: 3,
  formatted_address: 4,
};

/**
 * Sets an input field and a link or button to get the geographic
 * information of a location.
 * @param {String} opts The object containing the configuration for geocoding.
 * @param {String} opts.autoCompleteElement The id of the input field that will
 *                                          have an autocomplete dropdown of
 *                                          possible locations when a user
 *                                          types in it.
 * @param {String} opts.autoCompletePreference The value to be filled into the
 *                                             autoCompleteElement.
 *                                             Defaulted to formatted_address
 * @param {Function} opts.autoCompleteCallback Callback function after showing
 *                                             the autocomplete dropdown.
 * @param {Function} opts.reverseGeocodingElement The id of the link or button
 *                                                that would get the current
 *                                                browser location.
 * @param {Function} opts.reverseGeocodingSuccessCallback Callback function
 *                                                        after getting the
 *                                                        browser location.
 * @param {Function} opts.reverseGeocodingErrorCallback Callback function when
 *                                                      the browser location
 *                                                      cannot be determined.
 */
class Geocoding {
  constructor(opts) {
    this.config = opts;
    gmapPromise.then(google => {
      this.gmap = google.maps;
      this.autoComplete = new this.gmap.places.Autocomplete(
        /* @type {HTMLInputElement} */
        document.getElementById(this.config.autoCompleteElement),
        {},
      );
      this.gmap.event.addListener(this.autoComplete, 'place_changed', () => {
        if (this.config.autoCompleteCallback) {
          this.config.autoCompleteCallback(this.getSelectedAddressGeoInfo());
        }
      });

      if (this.config.initialLocation) {
        this.setInitialLocation(this.config.initialLocation);
      }
    });

    if (this.config.reverseGeocodingElement) {
      const reverseGeocodingElement = document.getElementById(
        this.config.reverseGeocodingElement,
      );
      reverseGeocodingElement.addEventListener('click', () =>
        this.reverseGeocodeCurrentLocation(),
      );
    }
  }

  reverseGeocodeCurrentLocation() {
    if (navigator.geolocation) {
      const geoOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      };

      navigator.geolocation.getCurrentPosition(
        position => {
          this.getReverseGeoInfo(
            position.coords.latitude,
            position.coords.longitude,
          );
        },
        err => {
          if (this.config.reverseGeocodingErrorCallback) {
            this.config.reverseGeocodingErrorCallback(
              `Failed to determine current location: ${err.toString()}`,
            );
          }
        },
        geoOptions,
      );
    }
  }

  setInitialLocation(location) {
    let geoInfo;

    if (!location.formatted_address) {
      if (location.latitude && location.longitude) {
        this.getReverseGeoInfo(location.latitude, location.longitude);
      }
      return;
    }

    const geocoder = new this.gmap.Geocoder();
    const params = { address: location.formatted_address };
    geocoder.geocode(params, (results, status) => {
      if (status === this.gmap.GeocoderStatus.OK) {
        geoInfo = this.formatGeoInfo(results[0]);
        const autoCompleteElement = document.getElementById(
          this.config.autoCompleteElement,
        );
        autoCompleteElement.value = geoInfo.formatted_address;
        if (this.config.reverseGeocodingSuccessCallback) {
          this.config.reverseGeocodingSuccessCallback(geoInfo);
        }
      } else if (location.latitude && location.longitude) {
        this.getReverseGeoInfo(location.latitude, location.longitude);
      }
    });
  }

  formatGeoInfo(place) {
    const geoInfo = {
      administrative_area_level_1: '',
      country: '',
      formatted_address: '',
      lat: '',
      lng: '',
      vicinity: '',
    };

    if (!place.address_components) {
      return false;
    }

    const addressComponentMap = {
      administrative_area_level_1: 'short_name',
      country: 'long_name',
      locality: 'long_name',
    };

    const geoInfoAddressComponents = place.address_components.reduce(
      (addressInfo, addressComponent) => {
        const addressType = addressComponent.types[0];
        if (addressComponentMap[addressType]) {
          addressInfo[addressType] =
            addressComponent[addressComponentMap[addressType]];
        }
        return addressInfo;
      },
      {},
    );

    Object.assign(geoInfo, geoInfoAddressComponents);

    geoInfo.formatted_address = place.formatted_address;
    geoInfo.lat = place.geometry.location.lat();
    geoInfo.lng = place.geometry.location.lng();
    geoInfo.vicinity =
      place.vicinity ||
      geoInfo.locality ||
      geoInfo.administrative_area_level_1 ||
      geoInfo.country ||
      '';
    return geoInfo;
  }

  getSelectedAddressGeoInfo() {
    const place = this.autoComplete.getPlace();
    return this.formatGeoInfo(place);
  }

  getReverseGeoInfo(latitude, longitude) {
    const geocoder = new this.gmap.Geocoder();
    const latlng = new this.gmap.LatLng(latitude, longitude);

    geocoder.geocode({ latLng: latlng }, (results, status) => {
      if (status === this.gmap.GeocoderStatus.OK) {
        const geoInfo = this.formatGeoInfo(results[0]);

        const autoCompleteElement = document.getElementById(
          this.config.autoCompleteElement,
        );
        if (
          this.config.autoCompletePreference === AUTOCOMPLETE_PREFERENCE.country
        ) {
          autoCompleteElement.value = geoInfo.country;
        } else if (
          this.config.autoCompletePreference ===
          AUTOCOMPLETE_PREFERENCE.locality
        ) {
          autoCompleteElement.value = geoInfo.locality;
        } else if (
          this.config.autoCompletePreference ===
          AUTOCOMPLETE_PREFERENCE.vicinity
        ) {
          autoCompleteElement.value = geoInfo.vicinity;
        } else {
          autoCompleteElement.value = geoInfo.formatted_address;
        }
        if (this.config.reverseGeocodingSuccessCallback) {
          this.config.reverseGeocodingSuccessCallback(geoInfo);
        }
      } else if (this.config.reverseGeocodingErrorCallback) {
        this.config.reverseGeocodingErrorCallback(status);
      }
    });
  }
}

/**
 * Factory method to create Geocoding class
 */
export default function createGeocoding(opts) {
  return new Geocoding(opts);
}
